<!-- 
History for a levy collection.
-->
<template>
  <div>
    <our-heading 
      :breadcrumbs="getBreadcrumbs('invoices')"
      :subtitle="statusDescription"
      title="Invoices"
      subtitle-tooltip="Levy collection status"
      user-guide-topic="levy-collection-invoices"
    >
      <template v-slot:navigator>
        <levy-collection-navigator :levy-collection-id="levyCollectionId" />
      </template>
    </our-heading>
    <our-search-bar v-if="hasInvoices">
      <our-quick-search 
        slot="quick-search"
        :criteria-mutation="criteriaMutation" 
        :value="quickSearch"
        label="Provider name or SAP doc ID" 
        field="providerName" 
        field-if-numeric="sapDocumentIdentifier" 
        @changed="$refs.invoices.refresh()"
      />
      <our-search-button 
        slot="search-button"
        :criteria="criteria" 
        :criteria-mutation="criteriaMutation" 
        :search-filters="searchFilters" 
        title="Search invoices"
        @changed="$refs.invoices.refresh()"
      />
      <our-saved-searches-menu 
        slot="saved-searches-button"
        :criteria="criteria"
        :criteria-mutation="criteriaMutation" 
        :clear-criteria-mutation="clearCriteriaMutation"
        module="invoices" 
        @selected="$refs.invoices.refresh()"
      />
    </our-search-bar>
    <invoice-listing
      v-show="hasInvoices" 
      ref="invoices" 
      :levy-collection-id="levyCollectionId" 
      default-no-data-text="This levy collection does not have any invoices yet."
      @click="openInvoice"
    />
    <our-getting-started-hint v-if="!hasInvoices">
      <p>
        Invoices will be listed here when at least one provider has had an invoice raised.
      </p>
      <p v-if="isInvoicing">
        Invoices can be raised from <i>Workflow</i> by running the <i>Raise invoice</i> task.
      </p>
      <template v-else>
        <p>
          The template used to generate the invoice PDF can be edited on the levy collection 
          <i>Setup invoicing</i> page.
        </p>
        <p class="text-xs-right">
          <v-btn :to="`/levy-collections/${levyCollectionId}/invoice-template-preview`" flat color="primary" target="_blank">
            Preview the invoice template
          </v-btn>
          <v-btn :to="`/levy-collections/${levyCollectionId}/setup/invoicing`" flat color="primary">Go to setup invoicing</v-btn>
        </p>
        <p>
          The levy collection will need to be at the <i>Invoicing</i> status before invoices can be raised.
          Go to <i>Workflow</i> to change the status of the levy collection.
        </p>
      </template>
      <p class="text-xs-right">
        <v-btn :to="`/levy-collections/${levyCollectionId}/workflow`" flat color="primary">Go to workflow</v-btn>
      </p>
    </our-getting-started-hint>
  </div>
</template>

<script>
import _ from "underscore";
import LevyCollectionNavigator from "@/components/levy-collections/LevyCollectionNavigator.vue";
import levyCollectionMixins from "./mixins";
import InvoiceListing from "@/components/invoices/InvoiceListing.vue";
import * as mutation from "@/store/invoices/mutation-types";
import { getSearchFilters } from "@/store/invoices/helpers";

export default {
  components: {
    LevyCollectionNavigator,
    InvoiceListing
  },
  mixins: [levyCollectionMixins],
  data() {
    return {
      criteriaMutation: mutation.LIST_SET_CRITERIA,
      clearCriteriaMutation: mutation.LIST_CLEAR_CRITERIA,
      redirectIfSandbox: true
    };
  },
  computed: {
    criteria() {
      return this.$store.state.invoices.listing.criteria;
    },
    quickSearch() {
      return this.criteria.sapDocumentIdentifier && !this.criteria.providerName
        ? this.criteria.sapDocumentIdentifier
        : this.criteria.providerName;
    },
    searchFilters() {
      const exclude = ["levyCollectionYear", "levyCollectionType"];
      return _(getSearchFilters()).filter(f => !exclude.includes(f.value));
    },
    hasInvoices() {
      return !this.$store.state.invoices.listing.empty;
    },
    isInvoicing() {
      return this.levyCollection.status == "Invoicing";
    }
  },
  methods: {
    openInvoice(invoiceId) {
      this.$router.push(`/levy-collections/${this.levyCollectionId}/invoices/${invoiceId}`);
    }
  }
};
</script>
